import { ChangeDetectionStrategy, Component, EventEmitter, Inject, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CuttingService, IRule, ISetups } from '@shared/services/cutting.service';
import { ValidatorPatterns } from '@shared/validatorpatterns';

@Component({
	selector: 'app-create-update-rule-dialog',
	templateUrl: './create-update-rule.html',
	styleUrls: ['./create-update-rule.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class CreateUpdateRuleDialogComponent implements OnInit {
	@Output() saved = new EventEmitter<void>();
	form: FormGroup;
	rule: IRule;
	loading = false;
	httpError = '';
	availableSetups: ISetups;

	constructor(
		@Inject(MAT_DIALOG_DATA) public editRule: IRule,
		public dialogRef: MatDialogRef<CreateUpdateRuleDialogComponent>,
		private cuttingService: CuttingService,
		private fb: FormBuilder,
	) {
		this.rule = {
			self: null,
			name: null,
			disableOptimization: false,
			useCoatingInOptimization: false,
			priority: null,
			description: null,
			batchType: null,
			material: null,
			profile: null,
			machine: null,
			setup: null,
			segregation: null,
			optimization: null,
			updatedDate: null,
			updatedBy: null,
			logisticsFormula: null,
		};

		if (editRule) {
			this.rule = { ...editRule };
		}
	}

	ngOnInit(): void {
		this.cuttingService.getSetups().subscribe((setups) => {
			this.availableSetups = setups;
		});
		this.initializeForm();
	}

	private initializeForm(): void {
		this.form = this.fb.group({
			name: [this.rule.name, [Validators.required, Validators.maxLength(50), Validators.pattern(ValidatorPatterns.AllowedCuttingCharacters)]],
			disableOptimization: [this.rule.disableOptimization],
			useCoatingInOptimization: [this.rule.useCoatingInOptimization],
			priority: [this.rule.priority ?? null, [Validators.min(0), Validators.pattern(ValidatorPatterns.WholeNumber)]],
			description: [this.rule.description, [Validators.maxLength(100), Validators.pattern(ValidatorPatterns.DanishCharacters)]],
			batchType: [this.rule.batchType, [Validators.maxLength(255), Validators.pattern(ValidatorPatterns.AllowedCuttingCharacters)]],
			material: [this.rule.material, [Validators.required, Validators.maxLength(255), Validators.pattern(ValidatorPatterns.AllowedCuttingCharacters)]],
			profile: [this.rule.profile || '', [Validators.maxLength(255), Validators.pattern(ValidatorPatterns.AllowedCuttingCharacters)]],
			machine: [this.rule.machine, [Validators.required, Validators.maxLength(255), Validators.pattern(ValidatorPatterns.AllowedCuttingCharacters)]],
			setup: [this.rule.setup, [Validators.required]],
			segregation: [this.rule.segregation, [Validators.maxLength(255), Validators.pattern(ValidatorPatterns.AllowedCuttingCharacters)]],
			logisticsFormula: [this.rule.logisticsFormula ?? '', [Validators.maxLength(1000)]],
		});

		this.subscribeToFormChanges();
	}

	private subscribeToFormChanges(): void {
		this.form.valueChanges.subscribe((value) => {
			Object.keys(value).forEach((key) => {
				if (key in this.rule) {
					this.rule[key] = value[key];
				}
			});
		});
	}

	get controls() {
		return this.form.controls;
	}

	onCancel(): void {
		this.dialogRef.close();
	}

	async onSave(): Promise<void> {
		this.httpError = '';
		if (this.form.invalid || this.loading) return;
		this.loading = true;
		const logisticsFormula = this.controls['logisticsFormula'].value;

		const saveObservable = this.rule.self ? this.cuttingService.updateRule(this.rule) : this.cuttingService.createRule(this.rule);

		saveObservable.subscribe({
			next: () => {
				this.saved.emit();
				this.dialogRef.close();
			},
			error: (errorResponse) => {
				this.httpError = `${errorResponse.status}`;
				// Only the API validates the formula, so its reason is shown at the field - unless the
				// formula was edited while saving, since the reason is about the one that was sent.
				const logisticsFormulaError = errorResponse.error?.errors?.logisticsFormula?.[0];
				if (logisticsFormulaError && this.controls['logisticsFormula'].value === logisticsFormula) {
					this.controls['logisticsFormula'].setErrors({ api: logisticsFormulaError });
				}
				this.loading = false;
			},
		});
	}
}
